import {
  ShipmentsMarker,
  LGPlusShipmentMarker,
  LGShipmentsMarker,
  APTShipmentsMarker,
  APTPlusShipmentMarker,
  TimeSensitiveShipmentsMarker,
  SGSPlusShipmentMarker,
  PupShipmentsMarker,
  RouteStopMarker,
  TimeSensitiveStopMarker,
  APTStopMarker,
  AnchorPointStopMarker,
  PupStopMarker,
  PupPlusStopMarker,
  LiftGateStopMarker,
  LGPlusStopMarker,
  // PDStopMarker,
  TrailerDropStopMarker,
  SGSPlusStopMarker,
  APTPlusStopMarker,
  SpecialInstructionMarker,
  SpecialInstructionStopMarker,
  GuaranteedMarker,
  PickupStopMarker,
  DeliveryStopMarker,
  WINGDeliveryStopMarker,
  WINGPickupStopMarker,
  APTDeliveryStopMarker,
  APTPickupStopMarker,
  APTPlusDeliveryStopMarker,
  APTPlusPickupStopMarker,
  DriverMarker,
  DriverArrivedAtStopMarker,
  DriverAtFuelingMarker,
  DriverAtLunchBreakMarker,
  ReturnToTerminalMarker,
} from "./Markers";
import "./Popups/style.scss";
import { formatDeliveryWindow, getDeliveryWindowByEarlyAndLateCTA } from "../dataGrid/gridUtilities";
import appConstants from "../../appConstants";
import Tooltip from "../common/StyledToolTip";
import {
  DRIVER_EVENTS,
} from "../modules/Dispatch/RouteExecution/constants";
import {
  getPendingStopStatus,
  isDriverCurrentStopPresent,
} from "../modules/Dispatch/utils/utils";
import { isPickupStop, getCustomerName } from "../modules/IBPlanning/Common/MergeStops/utills";

import { getPickupStatus, getPickupStatusTypeIcon } from "../modules/Dispatch/RouteExecution/utils/pickupStatusTypeIcon";

const MARKER_TYPE = {
  SGS12: "SGS12",
  SGS: "SGS",
  APT: "APT",
  LG: "L",
  AP: "AP",
  PUP: "PUP",
  TD: "TD",
  PUPLG: "PUP LG",
  WING: "WING",
  PTY: "PTY",
};

const PRIORITY_LIST = [MARKER_TYPE.WING, MARKER_TYPE.PTY];
const hasPriorityShipment = (stopData) =>
  stopData?.priority?.some((value) => PRIORITY_LIST.indexOf(value) > -1);

const getDispatchRouteStopsMarker = ({
  stop,
  color,
  borderColor,
  markerTextColor,
  isSelectedStop,
}) => {
  let params = {
    stopNo: stop.stopSequence,
    backgroundColor: isSelectedStop ? markerTextColor : color,
    borderColor: isSelectedStop ? color : borderColor,
    textColor: isSelectedStop ? color : markerTextColor,
    size: 22,
  };
  const marker =
    stop.stopType === appConstants.STOP_TYPE.Pickup
      ? getDispatchPickupStopMarker(stop)
      : getDispatchDeliveryStopMarker(stop);
  return <div>{withMarkerPros(marker, params)}</div>;
};

const getDispatchDeliveryStopMarker = (stopData) => {
  const constraints = [];
  if (hasPriorityShipment(stopData)) {
    constraints.push(MARKER_TYPE.WING);
  } else if (stopData?.isApt) {
    constraints.push(MARKER_TYPE.APT);
  }
  if (stopData?.isLiftGate) {
    constraints.push(MARKER_TYPE.LG);
  }
  if (constraints.length > 0) {
    return pickDeliveryStopMarker({
      markerType: constraints[0],
      hasMultipleConstraints: constraints.length > 1,
    });
  } else {
    return DeliveryStopMarker;
  }
};

const pickDeliveryStopMarker = ({ markerType, hasMultipleConstraints }) => {
  let marker = {};
  switch (markerType) {
    case MARKER_TYPE.WING:
      marker = WINGDeliveryStopMarker;
      break;
    case MARKER_TYPE.APT:
      marker = hasMultipleConstraints
        ? APTPlusDeliveryStopMarker
        : APTDeliveryStopMarker;
      break;
  }
  return marker;
};

const getDispatchPickupStopMarker = (stopData) => {
  const constraints = [];
  if (hasPriorityShipment(stopData)) {
    constraints.push(MARKER_TYPE.WING);
  } else if (stopData?.isApt) {
    constraints.push(MARKER_TYPE.APT);
  }
  if (stopData?.isLiftGate) {
    constraints.push(MARKER_TYPE.LG);
  }
  if (constraints.length > 0) {
    return pickPickupStopMarker({
      markerType: constraints[0],
      hasMultipleConstraints: constraints.length > 1,
    });
  } else {
    return PickupStopMarker;
  }
};

const pickPickupStopMarker = ({ markerType, hasMultipleConstraints }) => {
  let marker = {};
  switch (markerType) {
    case MARKER_TYPE.WING:
      marker = WINGPickupStopMarker;
      break;
    case MARKER_TYPE.APT:
      marker = hasMultipleConstraints
        ? APTPlusPickupStopMarker
        : APTPickupStopMarker;
      break;
  }
  return marker;
};

const getDispatchUnassignedPickupMarker = ({
  pickup,
  backgroundColor,
  borderColor,
  terminalOffset,
  theme,
}) => {
  const params = {
    theme,
    status: getPickupStatus(pickup, terminalOffset),
    backgroundColor,
    borderColor,
    size: 27,
  };
  const { marker, type } = getPickupStatusTypeIcon(params);
  return (
    <div>
      <div data-markername={type}>
        {marker}
      </div>
    </div>
  )
};

const getDispatchAssignedPickupMarker = ({
  stopData,
  isPickupActive,
  stopSequence,
  color,
  borderColor,
  markerTextColor,
}) => {
  let params = {
    stopNo: stopSequence,
    backgroundColor: isPickupActive ? markerTextColor : color,
    borderColor: isPickupActive ? color : borderColor,
    textColor: isPickupActive ? color : markerTextColor,
    size: 22,
  };
  const pickupsStopMarker = getDispatchPickupStopMarker(stopData);
  return (
    <div>
      <div data-markername={`${color}_${stopSequence}`}>
        {withMarkerPros(pickupsStopMarker, params)}
      </div>
    </div>
  );
};

const isDriverDelayedAtStop = (data) => {
  const arrivedStopId = data?.currentStatus?.stopId;
  const arrivedStopData = data?.stops?.find(
    (stop) => stop.stopId === arrivedStopId
  );
  return (
    getPendingStopStatus(
      arrivedStopData,
      arrivedStopData?.actualArrivalTime
    ) === appConstants.STOP_STATUS.DELAYED
  );
};

const getDispatchDriverMarker = (markerProps) => {
  const {
    data,
    theme,
    data: { stops, currentStatus },
    color,
  } = markerProps;
  if (
    data?.driverStatus === DRIVER_EVENTS.LunchBreak &&
    data?.currentStatus?.status === DRIVER_EVENTS.DelayStart
  ) {
    return <DriverAtLunchBreakMarker {...markerProps} />;
  }
  if (
    data?.driverStatus === DRIVER_EVENTS.FuelBreak &&
    data?.currentStatus?.status === DRIVER_EVENTS.DelayStart
  ) {
    return <DriverAtFuelingMarker {...markerProps} />;
  }
  if (data?.driverStatus === DRIVER_EVENTS.ReturnToTerminal) {
    return <ReturnToTerminalMarker {...markerProps} />;
  }
  if (
    data?.driverStatus === DRIVER_EVENTS.ArrivedAtStop &&
    isDriverCurrentStopPresent({ stops, currentStatus })
  ) {
    return (
      <DriverArrivedAtStopMarker
        color={
          isDriverDelayedAtStop(data)
            ? theme.violationsTextColor
            : color || theme.palette.primary.main
        }
        {...markerProps}
      />
    );
  }
  return <DriverMarker {...markerProps} />;
};

const getPickupTypeShipmentMarker = ({
  isActiveShipment,
  shipmentData,
  theme,
}) => {
  const params = {
    stopNo: null,
    backgroundColor: isActiveShipment
      ? appConstants.Colors.markerActiveColor
      : theme.HeaderSelectedText,
    borderColor: theme.AnchorTextColor,
    textColor: null,
    size: 22,
  };
  const markerName = "PickupShipmentsMarker";
  const deliveryWindowParams = {
    data: {
      priority: shipmentData.priority,
      apt: shipmentData.apt,
    },
    value: shipmentData.deliveryWindow,
  };
  const formattedDeliveryWindow =
    getDeliveryWindowByEarlyAndLateCTA(shipmentData) ||
    formatDeliveryWindow(deliveryWindowParams);
  const tooltip = [shipmentData.shipperName, formattedDeliveryWindow];
  return (
    <Tooltip
      title={
        <div>
          {shipmentData.shipperName}
          <br />
          {formattedDeliveryWindow}
        </div>
      }
    >
      <div
        data-markername={`${markerName}_${
          isActiveShipment ? "active" : "nonactive"
        }`}
        data-tooltip={JSON.stringify(tooltip)}
      >
        {withMarkerPros(PickupStopMarker, params)}
      </div>
    </Tooltip>
  );
};

const getShipmentMarker = ({shipmentData, theme, isActiveShipment}) => {
  if(!shipmentData.isDelivery){
    return getPickupTypeShipmentMarker({
      isActiveShipment,
      shipmentData,
      theme,
    });
  }
  const color = isActiveShipment ? appConstants.Colors.markerActiveColor : null;
  const constraints = [];
  if (shipmentData.priority) {
    if (appConstants.PRIORITY_SHIPMENTS_ICONS[shipmentData.priority]=== appConstants.PRIORITY_ICON.HIGH) {
      constraints.push(MARKER_TYPE.SGS12);
    } else {
      constraints.push(MARKER_TYPE.SGS);
    }
  } else if (shipmentData.apt && shipmentData.apt === MARKER_TYPE.APT) {
    constraints.push(MARKER_TYPE.APT);
  }
  if (shipmentData.liftgate === MARKER_TYPE.LG) {
    constraints.push(MARKER_TYPE.LG);
  }
  if (
    shipmentData.eqpmtPref &&
    (shipmentData.eqpmtPref.toUpperCase() === MARKER_TYPE.PUP ||
      shipmentData.eqpmtPref.toUpperCase() === MARKER_TYPE.PUPLG)
  ) {
    constraints.push(MARKER_TYPE.PUP);
  }

  const markerDimension = isActiveShipment ? "35px" : "23px";
  let markerName = "";
  if (constraints.length > 0) {
    return pickShipmentMarker({
      markerType: constraints[0],
      hasMultipleConstraints: constraints.length > 1,
      color: color,
      shipmentData,
      dimension: markerDimension,
      isActiveShipment,
    });
  } else {
    let marker = {};
    if (shipmentData?.shipmentSpecialInstruction) {
      marker = (
        <SpecialInstructionMarker
          dimension={markerDimension}
          backgroundColor={color}
        />
      );
      markerName = "SpecialInstructionMarker";
    } else {
      marker = (
        <ShipmentsMarker dimension={markerDimension} backgroundColor={color} />
      );
      markerName = "ShipmentsMarker";
    }
    const params = {
      data: {
        priority: shipmentData.priority,
        apt: shipmentData.apt,
      },
      value: shipmentData.deliveryWindow,
    };
    const tooltip = [
      shipmentData.isDelivery
        ? shipmentData.consigneeName
        : shipmentData.shipperName,
      formatDeliveryWindow(params),
    ];
    return (
      <Tooltip
        enterDelay={0}
        title={
          <div>
            {shipmentData.consigneeName}
            <br />
            {getDeliveryWindowByEarlyAndLateCTA(shipmentData) || formatDeliveryWindow(params)}
          </div>
        }
      >
        <div
          data-markername={`${markerName}_${
            isActiveShipment ? "active" : "nonactive"
          }`}
          data-tooltip={JSON.stringify(tooltip)}
        >
          {marker}
        </div>
      </Tooltip>
    );
  }
};

const pickShipmentMarker = ({
  markerType,
  hasMultipleConstraints,
  color,
  shipmentData,
  dimension,
  isActiveShipment,
}) => {
  let marker = {};
  let markerName = "";
  switch (markerType) {
    case MARKER_TYPE.SGS:
      marker = hasMultipleConstraints ? (
        <SGSPlusShipmentMarker backgroundColor={color} dimension={dimension} />
      ) : (
        <TimeSensitiveShipmentsMarker
          dimension={dimension}
          backgroundColor={color}
        />
      );
      markerName = hasMultipleConstraints
        ? "SGSPlusShipmentMarker"
        : "TimeSensitiveShipmentsMarker";
      break;
    case MARKER_TYPE.SGS12:
      marker = (
        <GuaranteedMarker backgroundColor={color} dimension={dimension} />
      );
      markerName = "GuaranteedMarker";
      break;
    case MARKER_TYPE.APT:
      marker = hasMultipleConstraints ? (
        <APTPlusShipmentMarker dimension={dimension} backgroundColor={color} />
      ) : (
        <APTShipmentsMarker dimension={dimension} backgroundColor={color} />
      );
      markerName = hasMultipleConstraints
        ? "APTPlusShipmentMarker"
        : "APTShipmentsMarker";
      break;
    case MARKER_TYPE.LG:
      marker = hasMultipleConstraints ? (
        <LGPlusShipmentMarker dimension={dimension} backgroundColor={color} />
      ) : (
        <LGShipmentsMarker dimension={dimension} backgroundColor={color} />
      );
      markerName = hasMultipleConstraints
        ? "LGPlusShipmentMarker"
        : "LGShipmentsMarker";
      break;
    case MARKER_TYPE.PUP:
      marker = (
        <PupShipmentsMarker dimension={dimension} backgroundColor={color} />
      );
      markerName = "PupShipmentsMarker";
      break;
    default:
      marker = (
        <ShipmentsMarker dimension={dimension} backgroundColor={color} />
      );
      markerName = "ShipmentsMarker";
      break;
  }
  const params = {
    data: {
      priority: shipmentData.priority,
      apt: shipmentData.apt,
    },
    value: shipmentData.deliveryWindow,
  };
  const customer = shipmentData.isDelivery ? shipmentData.consigneeName : shipmentData.shipperName;
  const tooltip = JSON.stringify([
    customer,
    formatDeliveryWindow(params),
  ]);
  return (
    <Tooltip
      title={
        <div>
          {customer}
          <br />
          {getDeliveryWindowByEarlyAndLateCTA(shipmentData) || formatDeliveryWindow(params)}
        </div>
      }
    >
      <div
        data-markername={`${markerName}_${
          isActiveShipment ? "active" : "nonactive"
        }`}
        data-tooltip={tooltip}
      >
        {marker}
      </div>
    </Tooltip>
  );
};

const getConstraints = (stopData) => {
  const constraints = [];
  const eqptType = [MARKER_TYPE.PUP, MARKER_TYPE.PUPLG];
  if (stopData.isGTDTDD || stopData.priority) {
    if (appConstants.PRIORITY_SHIPMENTS_ICONS[stopData.priority] === appConstants.PRIORITY_ICON.HIGH) {
      constraints.push(MARKER_TYPE.SGS12);
    } else {
      constraints.push(MARKER_TYPE.SGS);
    }
  } else if (stopData.apt && stopData.apt === MARKER_TYPE.APT) {
    constraints.push(MARKER_TYPE.APT);
  }
  if (stopData.liftgate === MARKER_TYPE.LG) {
    constraints.push(MARKER_TYPE.LG);
  }
  if (stopData.stopType === MARKER_TYPE.AP) {
    constraints.push(MARKER_TYPE.AP);
  }
  if (stopData.eqptPref && eqptType.includes(stopData.eqptPref)) {
    constraints.push(MARKER_TYPE.PUP);
  }
  // will be uncommented when markers become available
  // if(stopData.stopType === "P/D"){
  //   constraints.push("isPD");
  // }
  if (stopData.stopType === MARKER_TYPE.TD || stopData.isTrailerDrop) {
    constraints.push(MARKER_TYPE.TD);
  }
  return constraints;
};
const setProArray = (
  stopData,
  borderColor,
  markerTextColor,
  color,
  isSelectedStop
) => {
  return {
    stopNo: stopData.stopSequence,
    backgroundColor: isSelectedStop ? markerTextColor : color,
    border: isSelectedStop ? color : borderColor,
    textColor: isSelectedStop ? color : markerTextColor,
    dimension: isSelectedStop ? "40px" : "30px",
  };
};
const getRouteStopMarker = ({
  stopData,
  theme,
  borderColor,
  markerTextColor,
  color,
  isSelectedStop,
}) => {
  if(isPickupStop(stopData.stopType)){
      let params = {
        stopNo: stopData.stopSequence,
        backgroundColor: isSelectedStop ? markerTextColor : color,
        borderColor: isSelectedStop ? color : borderColor,
        textColor: isSelectedStop ? color : markerTextColor,
        size: 22,
      };
      return (
        <Tooltip
          title={
            <div>
              {stopData.shipperName}
              <br />
              ETA: {stopData.eta}
            </div>
          }
        >
          <div>{withMarkerPros(PickupStopMarker, params)}</div>
        </Tooltip>
      );
  }
  const propArray = setProArray(
    stopData,
    borderColor,
    markerTextColor,
    color,
    isSelectedStop
  );
  const constraints = getConstraints(stopData);
  if (constraints.length > 0) {
    return pickStopMarker(
      constraints[0],
      constraints.length > 1,
      stopData,
      propArray,
      theme,
      isSelectedStop
    );
  } else {
    let marker = {};
    if (stopData.specialInstruction) {
      propArray.dimension = isSelectedStop ? "50px" : "40px";
      marker = SpecialInstructionStopMarker;
    } else {
      marker = RouteStopMarker;
    }
    return (
      <Tooltip
        title={
          <div>
            {getCustomerName(stopData)}
            <br />
            ETA: {stopData.eta}
          </div>
        }
      >
        <div>{withMarkerPros(marker, propArray)}</div>
      </Tooltip>
    );
  }
};

const pickStopMarker = (
  markerType,
  hasMultipleConstraints,
  stopData,
  propArray,
  theme,
  isSelectedStop
) => {
  let marker = {};
  propArray.dimension = isSelectedStop ? "50px" : "40px";
  switch (markerType) {
    case MARKER_TYPE.SGS12:
      propArray.dimension = isSelectedStop ? "40px" : "30px";
      marker = GuaranteedMarker;
      break;
    case MARKER_TYPE.SGS:
      marker = hasMultipleConstraints
        ? SGSPlusStopMarker
        : TimeSensitiveStopMarker;
      break;
    case MARKER_TYPE.APT:
      marker = hasMultipleConstraints ? APTPlusStopMarker : APTStopMarker;
      break;
    case MARKER_TYPE.LG:
      marker = hasMultipleConstraints ? LGPlusStopMarker : LiftGateStopMarker;
      break;
    case MARKER_TYPE.PUP:
      marker = hasMultipleConstraints ? PupPlusStopMarker : PupStopMarker;
      break;
    case MARKER_TYPE.TD:
      marker = TrailerDropStopMarker;
      break;
    case MARKER_TYPE.AP:
      marker = AnchorPointStopMarker;
      break;
    default:
      marker = RouteStopMarker;
      break;
  }
  return (
    <Tooltip
      title={
        <div>
          {getCustomerName(stopData)}
          <br />
          ETA: {stopData.eta}
        </div>
      }
    >
      <div>{withMarkerPros(marker, propArray)}</div>
    </Tooltip>
  );
};

const withMarkerPros = (Component, props) => {
  return <Component {...props} />;
};

const getWayPoints = (routeStops) => {
  if (routeStops && routeStops.length > 0) {
    routeStops = routeStops.slice(); //mobxArray sort and reverse will not change array in place, recomended to use mobxArray.slice().sort() Ref:=> https://mobx.js.org/refguide/array.html
    return routeStops
      .sort((stop, nextStop) => stop.stopSequence - nextStop.stopSequence)
      .map((stop) => ({
        location: { lat: stop.latitude, lng: stop.longitude },
      }));
  }
  return null;
};

const getLastStop = (routeStops) => {
  if (routeStops && routeStops.length > 0) {
    routeStops = routeStops.slice(); // Need to slice an mobx observable array before applying any array functions
    const stops = routeStops.sort(
      (prevStop, currentStop) =>
        prevStop.stopSequence - currentStop.stopSequence
    );
    const lastStop = stops[stops.length - 1];
    return { lat: lastStop.latitude, lng: lastStop.longitude };
  }
  return null;
};

const getDispatchLastStop = (routeStops) => {
  if (routeStops && routeStops.length > 0) {
    routeStops = routeStops.slice(); // Need to slice an mobx observable array before applying any array functions
    const stops = routeStops.sort(
      (prevStop, currentStop) =>
        prevStop.stopSequence - currentStop.stopSequence
    );
    const lastStop = stops[stops.length - 1];
    return { lat: lastStop.latitude, lng: lastStop.longitude };
  }
  return null;
};

const getDispatchWayPoints = (routeStops) => {
  if (routeStops && routeStops.length > 0) {
    routeStops = routeStops.slice(); //mobxArray sort and reverse will not change array in place, recomended to use mobxArray.slice().sort() Ref:=> https://mobx.js.org/refguide/array.html
    return routeStops
      .sort((stop, nextStop) => stop.stopSequence - nextStop.stopSequence)
      .map((stop) => ({
        location: { lat: stop.latitude, lng: stop.longitude },
      }));
  }
  return null;
};

export {
  getDispatchUnassignedPickupMarker,
  getDispatchAssignedPickupMarker,
  getDispatchRouteStopsMarker,
  getDispatchWayPoints,
  getShipmentMarker,
  getWayPoints,
  getLastStop,
  getRouteStopMarker,
  getDispatchLastStop,
  getDispatchDriverMarker,
};
